/* eslint-disable react/no-this-in-sfc */
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';
import Highlighter from 'react-highlight-words';
import lunr from 'lunr';
import { buildList, startBuildList } from '../../actions/ListActions';
import ApiService from '../../services/ApiService';
import Search from '../Search';
import { LIST_ITEM_DELIMITER } from '../../util/form';
import { lunrEscapeQuery } from '../../util/search';
import { FormContainerContext, valueDecoder } from '../form/Form';

const defaultLimit = 25;

const defaultRenderSuggestion = (suggestion = {}, { query }) => {
  // Support for literal string suggestion
  if (suggestion && typeof suggestion !== 'object') {
    return <span>{suggestion}</span>;
  }

  if (suggestion.selectAll) {
    return <div style={{ fontWeight: 'bold' }}>Select All</div>;
  }

  const suggestionKeys = Object.keys(suggestion);
  const itemid = suggestionKeys[0];
  const name = suggestion.Name ? 'Name' : suggestionKeys[1];

  // Grab the first key after itemid
  // const name = _.without(Object.keys(suggestion), 'itemid')[0];
  const terms = query.replace(':', '').split(/\s/);

  const text = !suggestion[name] ? suggestion[itemid] : `${suggestion[itemid]}: ${suggestion[name]}`;

  return <Highlighter autoEscape highlightClassName="hl" searchWords={terms} textToHighlight={text} />;
};

const defaultGetSuggestionValue = (suggestion = {}) => {
  if (!suggestion) {
    return '';
  }

  // Support for literal string suggestion
  if (suggestion && typeof suggestion !== 'object') {
    return suggestion;
  }

  const suggestionKeys = Object.keys(suggestion);
  const itemid = suggestionKeys[0];
  const field = suggestion.Name ? 'Name' : suggestionKeys[1];

  // Get the first field excluding the itemid
  // const field = _.head(_.without(Object.keys(suggestion), 'itemid'));
  if (!field || !suggestion[field]) {
    return suggestion.itemid;
  }

  return `${suggestion[itemid]}:${LIST_ITEM_DELIMITER}${suggestion[field]}`;
};

const ListSearch = props => {
  const {
    allowSelectAll,
    clearValue,
    command = 'LIST',
    filter,
    filterSuggestions = s => s,
    getSuggestionValue = defaultGetSuggestionValue,
    highlightFirstSuggestion = true,
    initValue = true,
    list = {},
    loading,
    localSearch,
    onBlur,
    pageName,
    renderSuggestion = defaultRenderSuggestion,
    setLoading = () => {},
    skipAttachments = true,
    sort,
    url,
    validationKeyValue,
    ...rest
  } = props;

  const [suggestions, setSuggestions] = useState([]);
  const searchIndex = useRef(null);
  const searchData = useRef({});
  const { branchId } = useContext(FormContainerContext);
  const dispatch = useDispatch();
  const madeSelection = useRef(false);
  const working = useRef(true);

  const searchList = (value, limit) => {
    if (props.loadSuggestions) {
      return props.loadSuggestions(value, limit);
    }

    setLoading(true);

    madeSelection.current = '';
    working.current = true;

    return new Promise(resolve => {
      // List from a defined viewList page
      if (pageName) {
        const params = {
          cmd: command,
          filter,
          key: validationKeyValue,
          limit: limit || props.limit || defaultLimit,
          q: value || '',
          skipSearch: localSearch ? 1 : '',
          sort,
          skipAttachments,
        };

        if (branchId) {
          params.useBranchId = branchId;
        }

        const endpoint = url || `/list/${pageName}`;

        ApiService.get(endpoint, { params }).then(({ data }) => {
          resolve(data.list);
          setLoading(false);

          working.current = false;

          if (data.list) {
            if (data.length === 0) {
              clearValue();
            } else {
              const found = data.list.find(i => i.itemid === value);
              if (madeSelection.current === valueDecoder(value)) {
                if (found) {
                  debouncedBlur(found);
                } else {
                  clearValue();
                }
              }
            }
          }
        });
      }
    });
  };

  const handleBlur = suggestion => {
    madeSelection.current = props.value;

    if (working.current === true) {
      return;
    }

    if (suggestion) {
      if (suggestion.selectAll) {
        return onBlur();
      }

      return onBlur(getSuggestionValue(suggestion));
    }

    return onBlur(suggestion);
  };

  const debouncedBlur = _.debounce(handleBlur, 500);

  const getFilteredSuggestions = suggestions => {
    let filteredSuggestions = suggestions;

    if (filterSuggestions) {
      if (typeof filterSuggestions === 'function') {
        filteredSuggestions = filterSuggestions(suggestions);
      } else if (_.isPlainObject(filterSuggestions)) {
        // filter is simple key value object where all values have to match to pass filter
        // filteredSuggestions = suggestions.filter((obj) => {
        //   for (const key of Object.keys(filter)) {
        //     if (obj[key] !== filter[key]) {
        //       return false;
        //     }
        //   }
        //   return true;
        // });
      }
    }

    if (allowSelectAll && filteredSuggestions && filteredSuggestions.length) {
      filteredSuggestions = [{ name: 'Select All', selectAll: true }].concat(filteredSuggestions);
    }

    return filteredSuggestions || [];
  };

  const onSuggestionSelected = (event, data = {}) => {
    const { allowSelectAll } = props;
    const { suggestion } = data;

    if (suggestion === undefined) {
      props.onSuggestionSelected('');
      return;
    }

    if (allowSelectAll && suggestion.selectAll) {
      searchList('?', 1000).then(items => {
        const filtered = getFilteredSuggestions(items);
        for (const item of filtered) {
          if (!item.selectAll) {
            props.onSuggestionSelected(getSuggestionValue(item), item);
          }
        }
      });
      return;
    }

    props.onSuggestionSelected(getSuggestionValue(suggestion), suggestion);
  };

  const onSuggestionsClearRequested = () => {
    if (suggestions.length && (list.method || list.static)) {
      return;
    }

    setSuggestions([]);
  };

  const createIndex = (data = []) => {
    if (!Array.isArray(data) || !data.length) {
      return;
    }

    const fields = Object.keys(data[0]);
    const refField = fields[0];

    searchIndex.current = lunr(function () {
      this.pipeline.remove(lunr.stopWordFilter);
      this.pipeline.remove(lunr.stemmer);
      this.searchPipeline.remove(lunr.stopWordFilter);
      this.searchPipeline.remove(lunr.stemmer);

      fields.forEach(field => this.field(field));

      this.ref(refField); // this assumes first field is an id

      data.forEach(entry => this.add(entry));
    });

    searchData.current = _.keyBy(data, refField);
  };

  const searchLocalList = value => {
    if (value === '?' || value === '') {
      setSuggestions(_.values(getFilteredSuggestions(searchData.current)).slice(0, 50));
      return;
    }

    const results = searchIndex.current
      ? searchIndex.current.search(`${lunrEscapeQuery(value)}*`).slice(0, props.limit || defaultLimit)
      : [];

    const suggestions = [];
    results.forEach(result => {
      suggestions.push(searchData.current[result.ref]);
    });

    setSuggestions(getFilteredSuggestions(suggestions));
  };

  const loadSuggestions = value => {
    if (localSearch && !props.loadSuggestions) {
      searchLocalList(value);
    } else {
      searchList(value).then(suggestions => {
        setSuggestions(getFilteredSuggestions(suggestions));
      });
    }
  };

  const handleSubmit = value => {
    if (!highlightFirstSuggestion) {
      props.onSuggestionSelected(value, value);
    }
  };

  useEffect(() => {
    const listKey = validationKeyValue ? `${pageName}:${command}.${validationKeyValue}` : `${pageName}:${command}`;

    if (pageName) {
      // Check building state returned by action. Async mounted components may not have updated store at this point.
      const { building } = dispatch(startBuildList(pageName, command, validationKeyValue));
      if (!building[listKey]) {
        dispatch(buildList(pageName, command, validationKeyValue));
      }
    }

    // Attempting to initialize suggestion without data alt value
    if (props.value && initValue) {
      searchList(props.value).then(suggestions => {
        if (Array.isArray(suggestions) && suggestions.length) {
          for (const suggestion of suggestions) {
            if (
              (suggestion.itemid && suggestion.itemid === props.value) ||
              (suggestion.id && suggestion.id === parseInt(props.value))
            ) {
              props.onSuggestionSelected(getSuggestionValue(suggestion));
              break;
            }
          }
        }
      });
    }

    // Create local search index if flag is set
    // TODO localSearch doesn't work on higher order searches like AccountSearch etc
    if (localSearch && !props.loadSuggestions) {
      searchList('?', 5000).then(data => {
        createIndex(data);
      });
    }
  }, []);

  return (
    <Search
      {...rest}
      getSuggestionValue={getSuggestionValue}
      highlightFirstSuggestion={highlightFirstSuggestion}
      loadSuggestions={loadSuggestions}
      onBlur={handleBlur}
      onSubmit={handleSubmit}
      onSuggestionSelected={onSuggestionSelected}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      renderSuggestion={renderSuggestion}
      suggestions={suggestions}
    />
  );
};

ListSearch.propTypes = {
  allowSelectAll: PropTypes.bool,
  command: PropTypes.string,
  filter: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  filterSuggestions: PropTypes.func,
  getSuggestionValue: PropTypes.func,
  highlightFirstSuggestion: PropTypes.bool,
  initValue: PropTypes.bool,
  limit: PropTypes.number,
  list: PropTypes.object,
  loadSuggestions: PropTypes.func,
  localSearch: PropTypes.bool,
  onBlur: PropTypes.func,
  onSuggestionSelected: PropTypes.func,
  pageName: PropTypes.string,
  renderSuggestion: PropTypes.func,
  setLoading: PropTypes.func,
  sort: PropTypes.object,
  validationKeyValue: PropTypes.string,
  value: PropTypes.string,
};

export default ListSearch;
